import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useLang } from "@/contexts/LangContext";
import MotionReveal from "@/components/MotionReveal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import api from "@/lib/api";
import { Camera } from "lucide-react";

const ALLOWED = [".jpg", ".jpeg", ".png", ".webp", ".heic", ".heif", ".gif"];

const Upload = () => {
  const { t, lang } = useLang();
  const inputRef = useRef(null);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [doneCount, setDoneCount] = useState(0);
  const [success, setSuccess] = useState(false);

  const handleFiles = async (files) => {
    const arr = Array.from(files || []);
    if (arr.length === 0) return;
    // Validate types
    for (const f of arr) {
      const ext = "." + (f.name.split(".").pop() || "").toLowerCase();
      if (!ALLOWED.includes(ext)) {
        toast.error(t("upload.errorType"));
        return;
      }
    }
    setBusy(true);
    setProgress(0);
    let okCount = 0;
    try {
      for (let i = 0; i < arr.length; i += 1) {
        const file = arr[i];
        const fd = new FormData();
        fd.append("file", file);
        if (name) fd.append("uploader", name);
        await api.post("/photos", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (e) => {
            if (!e.total) return;
            const pct = ((i + e.loaded / e.total) / arr.length) * 100;
            setProgress(Math.min(99, pct));
          },
        });
        okCount += 1;
      }
      setProgress(100);
      setDoneCount(okCount);
      setSuccess(true);
      toast.success(t("upload.successTitle"));
    } catch (_e) {
      toast.error(t("upload.errorGeneric"));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  if (success) {
    return (
      <div className="mx-auto max-w-[680px] px-5 sm:px-8 pt-24 sm:pt-32 pb-24" data-testid="upload-page">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.2, 0.8, 0.2, 1] }}
          data-testid="upload-success-state"
        >
          <p className="editorial-kicker text-accent">{t("upload.overline")}</p>
          <h1 className="editorial-h1 mt-6 text-4xl sm:text-6xl">{t("upload.successTitle")}</h1>
          <p className="mt-6 text-foreground/75 leading-relaxed max-w-[44ch]">{t("upload.successBody")}</p>
          <p className="editorial-kicker mt-6">{doneCount} · photos</p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Button
              type="button"
              onClick={() => {
                setSuccess(false);
                setProgress(0);
                setDoneCount(0);
              }}
              data-testid="upload-add-another"
              className="h-12 px-6 rounded-xl bg-foreground text-background hover:bg-foreground/90 tracking-[0.22em] uppercase text-[12px]"
            >
              {t("upload.addAnother")}
            </Button>
            <Link
              to={`/${lang}/gallery`}
              data-testid="upload-view-gallery"
              className="inline-flex items-center h-12 px-6 rounded-xl border border-border text-[12px] tracking-[0.22em] uppercase hover:border-accent hover:text-accent transition-colors"
            >
              {t("upload.viewGallery")}
            </Link>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div data-testid="upload-page">
      <section className="mx-auto max-w-[680px] px-5 sm:px-8 pt-24 sm:pt-32">
        <MotionReveal>
          <p className="editorial-kicker">{t("upload.overline")}</p>
        </MotionReveal>
        <MotionReveal delay={0.05}>
          <h1 className="editorial-h1 mt-6 text-4xl sm:text-6xl">{t("upload.title")}</h1>
        </MotionReveal>
        <MotionReveal delay={0.1}>
          <p className="mt-6 text-foreground/75 leading-relaxed max-w-[44ch]">{t("upload.body")}</p>
        </MotionReveal>
      </section>

      <section className="mx-auto max-w-[680px] px-5 sm:px-8 mt-12 pb-24" data-testid="guest-photo-upload">
        <div className="space-y-3">
          <Label htmlFor="uploader" className="editorial-kicker">{t("upload.nameLabel")}</Label>
          <Input
            id="uploader"
            value={name}
            onChange={(e) => setName(e.target.value)}
            data-testid="guest-photo-upload-name-input"
            className="h-12 border-0 border-b border-border rounded-none bg-transparent focus-visible:ring-0 focus-visible:border-accent"
          />
        </div>

        <div className="mt-10 surface-card p-8 sm:p-12 text-center">
          <Camera className="mx-auto w-10 h-10 text-foreground/40" />
          <input
            ref={inputRef}
            type="file"
            multiple
            accept="image/*,.heic,.heif"
            onChange={(e) => handleFiles(e.target.files)}
            data-testid="guest-photo-upload-file-input"
            className="sr-only"
          />
          <Button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={busy}
            data-testid="guest-photo-upload-submit-button"
            className="mt-6 h-12 px-8 rounded-xl bg-foreground text-background hover:bg-foreground/90 tracking-[0.22em] uppercase text-[12px]"
          >
            {busy ? t("upload.uploading") : t("upload.choose")}
          </Button>
          {busy && (
            <div className="mt-6" data-testid="guest-photo-upload-progress">
              <Progress value={progress} className="h-1 bg-border" />
              <p className="editorial-kicker mt-3">{Math.round(progress)}%</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Upload;
